/** Public Menu allergen matrix (Demo Mode) - a "pseudo public" presentation of a menu's
 * recipes, still inside the authenticated app (gated by the existing `MenusRead` permission),
 * but visually/content-wise aimed at an external/customer-facing look: no costs, margins or
 * internal ids. Mocked data only, per the Demo Screen Structure pattern - resolves recipes the
 * same way MenuDetail/useMenuDetail does (recipeId -> RecipeV2 catalogue lookup), but reads the
 * mock catalogues directly rather than through the typed menus client, since both source arrays
 * are already Demo Mode data. */

import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Stack, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { ArrowLeft } from '../../../assets';
import { menus } from '../../../mocks/data/menusData';
import { mockRecipesV2, mockAllergens } from '../../../mocks/data/recipeV2Data';
import AllergenMatrixCell, { MatrixAllergenStatus } from './components/AllergenMatrixCell';
import { DietaryCategory } from './types';

const PublicMenuMatrix = () => {
  const { t } = useTranslation('publicMenu');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const menu = useMemo(() => menus.find((m) => m.id === Number(id)), [id]);

  const rows = useMemo(() => {
    if (!menu) return [];
    return menu.courses.flatMap((course) =>
      course.dishes.map((dish) => {
        const recipe = mockRecipesV2.find((r) => r.id === dish.recipeId);
        return { courseName: course.name, dish, recipe };
      }),
    ).filter((row) => row.recipe);
  }, [menu]);

  if (!menu) {
    return (
      <PageArea>
        <Typography variant="textMD">{t('notFound', 'Menu not found')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(`/range/menus/${menu.id}`)}
          aria-label={t('backToMenu', 'Back to menu')}>
          <SvgIcon><ArrowLeft /></SvgIcon>
        </IconButton>
        <PageTitle subTitle={t('subtitle', 'Allergen information for every dish on this menu')}>
          {menu.name}
        </PageTitle>
      </Stack>

      <Section style={{ p: 0 }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                  {t('table.dish', 'Dish')}
                </TableCell>
                {mockAllergens.map((allergen) => (
                  <TableCell key={allergen.code}
                    align="center"
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {allergen.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ courseName, dish, recipe }) => (
                <TableRow key={dish.recipeId}>
                  <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: 'background.paper' }}>
                    <Typography
                      variant="textSM"
                      fontWeight={600}
                      onClick={() => navigate(`/menus/${menu.id}/public/recipes/${dish.recipeId}`)}
                      sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {recipe!.customerFacingName ?? recipe!.description}
                    </Typography>
                    <Typography variant="textXS"
                      color="text.secondary">
                      {courseName}
                    </Typography>
                  </TableCell>
                  {mockAllergens.map((allergen) => {
                    const attribute = recipe?.dietaryAttributes?.find(
                      (a) => a.category === DietaryCategory.Allergen && a.code === allergen.code,
                    );
                    const status = (attribute?.status as MatrixAllergenStatus) ?? 'FreeFrom';
                    return (
                      <TableCell key={allergen.code}
                        align="center">
                        <AllergenMatrixCell
                          allergenCode={allergen.code}
                          status={status}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Box sx={{ mt: 2 }}>
        <Typography variant="textXS"
          color="text.secondary">
          {t('legend', 'Icon = Contains or May Contain (traces). Grey circle = Does Not Contain.')}
        </Typography>
      </Box>
    </PageArea>
  );
};

export default PublicMenuMatrix;
